import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useRouteError } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorScreen } from './shared/ErrorScreen.js';

/** Shared functional content so both the route element and the class boundary render identically. */
function RouteErrorContent({ error }: { error: unknown }) {
  const { t } = useTranslation();
  const message = error instanceof Error ? error.message : String(error ?? '');
  const showStack = import.meta.env.DEV;

  return (
    <ErrorScreen
      code={t('errors.appError.code', { defaultValue: 'Oops' })}
      bubble={t('errors.appError.bubble', { defaultValue: 'Something threw me off — but your data is safe.' })}
      title={t('errorPage.title', { defaultValue: 'Something went wrong' })}
      message={t('errorPage.body', {
        defaultValue: 'An unexpected error interrupted this page. Reloading usually fixes it.',
      })}
      detail={showStack && message ? message : null}
      actions={[
        {
          label: t('errorPage.reload', { defaultValue: 'Reload page' }),
          // Full reload, not SPA routing — the router itself may have crashed.
          onClick: () => window.location.reload(),
          variant: 'primary',
          icon: 'rotate-ccw',
        },
        {
          label: t('errorPage.backToDashboard', { defaultValue: 'Go to dashboard' }),
          onClick: () => {
            window.location.href = '/dashboard';
          },
          variant: 'secondary',
          icon: 'home',
        },
      ]}
    />
  );
}

/** Router-level error element: catches loader/render errors inside routes. */
export function RouteErrorPage() {
  const error = useRouteError();
  return <RouteErrorContent error={error} />;
}

/**
 * Last-resort boundary around the whole tree (RouterProvider included) for
 * errors the router can't catch. Class component — React error boundaries
 * have no hook equivalent.
 */
export class AppErrorBoundary extends Component<{ children: ReactNode }, { error: unknown | null }> {
  state: { error: unknown | null } = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Uncaught app error', error, info);
  }

  render() {
    if (this.state.error !== null) {
      return <RouteErrorContent error={this.state.error} />;
    }
    return this.props.children;
  }
}
